import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PayrollWorkbench from '../components/PayrollWorkbench';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ymd = (d) => d.toISOString().slice(0, 10);

// Money → Payroll → Process & Push (Oct 2026): every payroll-processing tool —
// stats, pre-lock checklist, lock/unlock, Push to QuickBooks (preview, re-run,
// compare, CSV backup, confirm). Reports keeps only time-entry viewing/editing.
export default function PayrollProcess() {
  const { user, token } = useAuth();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [payPeriod, setPayPeriod] = useState(null);
  const [periodKey, setPeriodKey] = useState(qs.get('start') ? 'custom' : 'current');
  const [period, setPeriod] = useState({
    start: qs.get('start') || ymd(new Date(Date.now() - 13 * 86400000)),
    end: qs.get('end') || ymd(new Date()),
  });
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const isAdminUser = user?.role === 'admin' || user?.is_super_user;

  useEffect(() => {
    if (!token || !isAdminUser) return;
    axios.get(`${API}/company/pay-period`, auth)
      .then(({ data: d }) => {
        setPayPeriod(d);
        if (d.configured && d.current && !qs.get('start')) setPeriod({ start: d.current.start, end: d.current.end });
      })
      .catch(() => setPayPeriod({ configured: false }));
  }, [token]); // eslint-disable-line

  const fetchData = useCallback(async () => {
    if (!period.start || !period.end) return;
    setLoading(true); setErr(null);
    try {
      const params = { start_date: period.start, end_date: period.end, page, page_size: 50 };
      if (query.trim()) params.q = query.trim();
      const res = await axios.get(`${API}/reports/v2/payroll`, { ...auth, params });
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load payroll data');
      setData(null);
    } finally { setLoading(false); }
  }, [token, period.start, period.end, page, query]); // eslint-disable-line

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [query, period.start, period.end]);

  const patchRow = useCallback((workerId, row) => {
    setData((d) => {
      if (!d?.rows) return d;
      const rows = row ? d.rows.map((r) => (r.worker_id === workerId ? row : r)) : d.rows.filter((r) => r.worker_id !== workerId);
      return { ...d, rows };
    });
  }, []);

  const pickPeriod = (key) => {
    setPeriodKey(key);
    if (key === 'current' && payPeriod?.current) setPeriod({ start: payPeriod.current.start, end: payPeriod.current.end });
    else if (key === 'previous' && payPeriod?.previous) setPeriod({ start: payPeriod.previous.start, end: payPeriod.previous.end });
    else if (key === 'current' && !payPeriod?.configured) setPeriod({ start: ymd(new Date(Date.now() - 13 * 86400000)), end: ymd(new Date()) });
  };

  if (user && !isAdminUser) return <Navigate to="/home" replace />;
  const inputStyle = { background: '#1A2433', color: '#fff', border: '1px solid #2D3748' };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="payroll-process-page">
      <div>
        <h1 className="text-2xl font-semibold text-white flex items-center gap-2"><Send className="w-5 h-5 text-[#1E88E5]" /> Process &amp; Push</h1>
        <p className="text-sm mt-0.5" style={{ color: '#A0AEC0' }}>
          Run the pre-lock checklist, lock the period, then build a QuickBooks preview, compare, back up a CSV and confirm the push.
          To view or fix individual time entries, use Reports → Payroll Report.
        </p>
      </div>

      <div className="ht-card p-3 flex flex-wrap items-center gap-2" data-testid="process-period-selector">
        {[['current', 'Current Period'], ['previous', 'Previous Period'], ['custom', 'Custom']].map(([k, l]) => (
          <button key={k} onClick={() => pickPeriod(k)} className="px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{ background: periodKey === k ? '#1E88E5' : 'transparent', color: periodKey === k ? '#fff' : '#A0AEC0', border: `1px solid ${periodKey === k ? '#1E88E5' : '#2D3748'}` }}
            data-testid={`process-period-${k}`}>{l}</button>
        ))}
        {periodKey === 'custom' && (
          <div className="flex items-center gap-2 ml-1">
            <input type="date" value={period.start} onChange={(e) => setPeriod({ ...period, start: e.target.value })} className="px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="process-period-start" />
            <span style={{ color: '#64748B' }}>→</span>
            <input type="date" value={period.end} onChange={(e) => setPeriod({ ...period, end: e.target.value })} className="px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="process-period-end" />
          </div>
        )}
        <span className="ml-auto text-xs" style={{ color: '#64748B' }} data-testid="process-period-label">
          {period.start} → {period.end}
          {payPeriod && !payPeriod.configured && ' · no pay-period cycle configured (using last 14 days)'}
        </span>
      </div>

      {err && <p className="text-sm text-rose-400" data-testid="process-error">{err}</p>}
      {loading && !data && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading payroll…</div>}
      {data && (
        <PayrollWorkbench
          mode="payroll"
          data={data}
          token={token}
          startDate={period.start}
          endDate={period.end}
          onRefresh={fetchData}
          onPatchRow={patchRow}
          serverPaging={{ page, query, onPageChange: setPage, onQueryChange: setQuery, remember: false, onReset: () => { setQuery(''); setPage(1); } }}
        />
      )}
    </div>
  );
}
